/* eslint-disable no-param-reassign */
import { dateStrToInt } from '@/helpers/dateTime';
import shuttleRidersService from '@/modules/Flexigo/services/shuttleRiders';
import workgroupService from '@/modules/Flexigo/services/workgroup';
import {
  DUMMY_ASSOCIATIONS,
  DUMMY_ASSIGNMENTS,
  DUMMY_WORKGROUPS,
} from '@/modules/Flexigo/screens/ShuttleRiders/__fixtures__/shuttleRiders';

// TODO: remove once the BE endpoints are live
const USE_DUMMY_DATA = false;

function buildRowId(row) {
  // walkingDistanceInMeter is part of the association identity because the BE can return
  // multiple candidate stops for the same rider/route group (routeId/stationId null) and
  // ag-grid requires every row id to be unique.
  const distance = row.walkingDistanceInMeter == null ? 'null' : row.walkingDistanceInMeter;
  return `${row.personnelId}-${row.routeGroupId || 0}-${row.routeId || 0}-${row.stationId || 0}-${distance}`;
}

// BE returns Assignment as { id, startDate, endDate, association: {...Association fields} }.
// Flatten it so components can keep treating rows like flat Association records, while
// preserving the real assignment id (needed for delete) and the date range.
function flattenAssignment(row) {
  const { id, startDate, endDate, association = {} } = row;
  return { ...association, id, startDate, endDate };
}

// BE's delete-assignments endpoint expects the nested Assignment shape it originally
// returned (it reads assignment.id, falling back to assignment.association.routeGroupId
// / personnelId when no reservation batch is found for that id).
function toAssignmentPayload(row) {
  return {
    id: row.id,
    association: {
      routeGroupId: row.routeGroupId,
      personnelId: row.personnelId,
    },
  };
}

function hasServerFilterCriteria(filters = {}) {
  const {
    selectedCompanies = [],
    selectedDepartments = [],
    selectedRouteGroups = [],
    selectedSchedules = [],
  } = filters;
  return Boolean(
    selectedCompanies.length || selectedDepartments.length || selectedRouteGroups.length || selectedSchedules.length,
  );
}

// AssociationsTab only reads the filtered `associations` state when server filter criteria
// are active (otherwise it falls back to `allAssociations`), so refreshing `fetchAssociations`
// without criteria would be a redundant duplicate of `fetchAllAssociations`.
function getAssociationsRefreshDispatches(dispatch, filters) {
  const requests = [dispatch('fetchAllAssociations')];
  if (hasServerFilterCriteria(filters)) requests.push(dispatch('fetchAssociations', filters));
  return requests;
}

const state = () => ({
  associations: [],
  allAssociations: [],
  assignments: [],
  allAssignments: [],
  workgroups: [],
  isLoadingAssociations: false,
  isLoadingAssignments: false,
  isLoadingAllAssociations: false,
  isLoadingAllAssignments: false,
  isLoadingWorkgroups: false,
  // Cache of the last user-selected date range on the Assignments tab so it
  // survives tab switches (the URL query keys are stripped when the tab is
  // inactive — see ShuttleRiders.vue#syncTabQuery).
  lastAssignmentDateRange: null,
  error: null,
});

const mutations = {
  SET_ASSOCIATIONS(storeState, payload) {
    storeState.associations = payload;
  },
  SET_ALL_ASSOCIATIONS(storeState, payload) {
    storeState.allAssociations = payload;
  },
  SET_WORKGROUPS(storeState, payload) {
    storeState.workgroups = payload;
  },
  SET_ASSIGNMENTS(storeState, payload) {
    storeState.assignments = payload;
  },
  SET_ALL_ASSIGNMENTS(storeState, payload) {
    storeState.allAssignments = payload;
  },
  SET_IS_LOADING_ASSOCIATIONS(storeState, payload) {
    storeState.isLoadingAssociations = payload;
  },
  SET_IS_LOADING_ASSIGNMENTS(storeState, payload) {
    storeState.isLoadingAssignments = payload;
  },
  SET_IS_LOADING_ALL_ASSIGNMENTS(storeState, payload) {
    storeState.isLoadingAllAssignments = payload;
  },
  SET_IS_LOADING_ALL_ASSOCIATIONS(storeState, payload) {
    storeState.isLoadingAllAssociations = payload;
  },
  SET_IS_LOADING_WORKGROUPS(storeState, payload) {
    storeState.isLoadingWorkgroups = payload;
  },
  SET_ERROR(storeState, payload) {
    storeState.error = payload;
  },
  CLEAR_ERROR(storeState) {
    storeState.error = null;
  },
  SET_LAST_ASSIGNMENT_DATE_RANGE(storeState, payload) {
    storeState.lastAssignmentDateRange = payload;
  },
};

const actions = {
  async fetchAssociations({ commit }, filters = {}) {
    commit('SET_IS_LOADING_ASSOCIATIONS', true);
    commit('CLEAR_ERROR');
    try {
      let list;
      if (USE_DUMMY_DATA) {
        list = DUMMY_ASSOCIATIONS;
      } else {
        const {
          selectedCompanies = [],
          selectedDepartments = [],
          selectedRouteGroups = [],
          selectedSchedules = [],
        } = filters;
        const response = await shuttleRidersService.listAssociations({
          companyIds: selectedCompanies.map(({ id }) => id),
          departments: selectedDepartments.map((d) => d.id),
          routeGroups: selectedRouteGroups.filter((rg) => rg.id !== null).map((rg) => rg.id),
          schedules: selectedSchedules.map((s) => s.value),
        });
        list = Array.isArray(response) ? response : response?.response || [];
      }
      commit(
        'SET_ASSOCIATIONS',
        list.map((row) => ({ ...row, id: buildRowId(row) })),
      );
    } catch (error) {
      console.error('Failed to load associations', error);
      commit('SET_ERROR', error?.message || 'Failed to load associations');
      throw error;
    } finally {
      commit('SET_IS_LOADING_ASSOCIATIONS', false);
    }
  },

  async fetchAllAssociations({ commit }) {
    commit('SET_IS_LOADING_ALL_ASSOCIATIONS', true);
    commit('CLEAR_ERROR');
    try {
      let list;
      if (USE_DUMMY_DATA) {
        list = DUMMY_ASSOCIATIONS;
      } else {
        const response = await shuttleRidersService.listAssociations({});
        list = Array.isArray(response) ? response : response?.response || [];
      }
      commit(
        'SET_ALL_ASSOCIATIONS',
        list.map((row) => ({ ...row, id: buildRowId(row) })),
      );
    } catch (error) {
      console.error('Failed to load all associations', error);
      commit('SET_ERROR', error?.message || 'Failed to load all associations');
      throw error;
    } finally {
      commit('SET_IS_LOADING_ALL_ASSOCIATIONS', false);
    }
  },

  async fetchWorkgroups({ commit }) {
    commit('SET_IS_LOADING_WORKGROUPS', true);
    commit('CLEAR_ERROR');
    try {
      if (USE_DUMMY_DATA) {
        commit('SET_WORKGROUPS', DUMMY_WORKGROUPS);
      } else {
        // Only shuttle route groups that are currently active are relevant here.
        const response = await workgroupService.getWorkgroups('SHUTTLE', undefined, true);
        commit('SET_WORKGROUPS', Array.isArray(response) ? response : response?.response || []);
      }
    } catch (error) {
      console.error('Failed to load workgroups', error);
      commit('SET_ERROR', error?.message || 'Failed to load workgroups');
      throw error;
    } finally {
      commit('SET_IS_LOADING_WORKGROUPS', false);
    }
  },

  async fetchAssignments({ commit }, filters = {}) {
    commit('SET_IS_LOADING_ASSIGNMENTS', true);
    commit('CLEAR_ERROR');
    try {
      let list;
      if (USE_DUMMY_DATA) {
        list = DUMMY_ASSIGNMENTS;
      } else {
        const {
          startDay,
          endDay,
          selectedDepartments = [],
          selectedRouteGroups = [],
          selectedSchedules = [],
        } = filters;
        const response = await shuttleRidersService.listAssignments({
          startDay,
          endDay,
          departments: selectedDepartments.map((d) => d.id),
          routeGroups: selectedRouteGroups.filter((rg) => rg.id !== null).map((rg) => rg.id),
          schedules: selectedSchedules.map((s) => s.value),
        });
        list = Array.isArray(response) ? response : response?.response || [];
      }
      // Dummy fixtures are already flat; only live BE responses need unwrapping.
      commit('SET_ASSIGNMENTS', USE_DUMMY_DATA ? list : list.map(flattenAssignment));
    } catch (error) {
      console.error('Failed to load assignments', error);
      commit('SET_ERROR', error?.message || 'Failed to load assignments');
      throw error;
    } finally {
      commit('SET_IS_LOADING_ASSIGNMENTS', false);
    }
  },

  async fetchAllAssignments({ commit }, { startDay, endDay } = {}) {
    commit('SET_IS_LOADING_ALL_ASSIGNMENTS', true);
    commit('CLEAR_ERROR');
    try {
      let list;
      if (USE_DUMMY_DATA) {
        list = DUMMY_ASSIGNMENTS;
      } else {
        const response = await shuttleRidersService.listAssignments({ startDay, endDay });
        list = Array.isArray(response) ? response : response?.response || [];
      }
      commit('SET_ALL_ASSIGNMENTS', USE_DUMMY_DATA ? list : list.map(flattenAssignment));
    } catch (error) {
      console.error('Failed to load all assignments', error);
      commit('SET_ERROR', error?.message || 'Failed to load all assignments');
      throw error;
    } finally {
      commit('SET_IS_LOADING_ALL_ASSIGNMENTS', false);
    }
  },

  async assignPersonnels({ commit, dispatch }, { startDate, endDate = null, associations, filters }) {
    try {
      // BE silently skips any association missing routeGroupId/routeId/stationId/schedule
      // when a date range is provided — filter here so we can tell the user how many
      // of their selected rows will actually be assigned.
      const assignable = associations.filter((row) => row.routeGroupId && row.routeId && row.stationId && row.schedule);
      await shuttleRidersService.assignPersonnels({
        startDate: dateStrToInt(startDate),
        endDate: endDate ? dateStrToInt(endDate) : null,
        associations: assignable,
      });
      await Promise.all(getAssociationsRefreshDispatches(dispatch, filters));
      return { assignedCount: assignable.length, skippedCount: associations.length - assignable.length };
    } catch (error) {
      console.error('Assign failed', error);
      commit('SET_ERROR', error?.message || 'Assign failed');
      throw error;
    }
  },

  async addToGroup({ commit, dispatch }, { routeGroupId, routeGroupName, associations, filters }) {
    try {
      // BE's /suggest is a fire-and-forget write (returns no body to act on). For
      // REGULAR-schedule riders it re-associates using association.routeGroupId as-is,
      // so we must stamp the newly selected group onto each row before sending —
      // otherwise it silently re-associates them with their existing group.
      const updatedAssociations = associations.map((row) => ({
        ...row,
        routeGroupId,
        routeGroup: routeGroupName,
      }));
      await shuttleRidersService.suggest({
        associatedRouteGroupId: routeGroupId,
        associations: updatedAssociations,
      });
      await Promise.all(getAssociationsRefreshDispatches(dispatch, filters));
    } catch (error) {
      console.error('Add to group failed', error);
      commit('SET_ERROR', error?.message || 'Add to group failed');
      throw error;
    }
  },

  async deleteAssignments({ commit, dispatch }, { assignments, filters }) {
    try {
      await shuttleRidersService.deleteAssignments(assignments.map(toAssignmentPayload));
      await Promise.all([
        dispatch('fetchAssignments', filters),
        dispatch('fetchAllAssignments', { startDay: filters.startDay, endDay: filters.endDay }),
      ]);
    } catch (error) {
      console.error('Delete assignments failed', error);
      commit('SET_ERROR', error?.message || 'Delete assignments failed');
      throw error;
    }
  },
};

const getters = {
  associations: (storeState) => storeState.associations,
  assignments: (storeState) => storeState.assignments,
  workgroups: (storeState) => storeState.workgroups,
  isLoadingAssociations: (storeState) => storeState.isLoadingAssociations,
  isLoadingAssignments: (storeState) => storeState.isLoadingAssignments,
  isLoadingAllAssociations: (storeState) => storeState.isLoadingAllAssociations,
  isLoadingAllAssignments: (storeState) => storeState.isLoadingAllAssignments,
  isLoadingWorkgroups: (storeState) => storeState.isLoadingWorkgroups,
  lastAssignmentDateRange: (storeState) => storeState.lastAssignmentDateRange,
  error: (storeState) => storeState.error,
  allRouteGroupOptions: (storeState) => {
    const map = new Map();
    storeState.workgroups.forEach(({ id, name }) => {
      if (id && !map.has(id)) map.set(id, { id, name: name || id });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
/* eslint-enable no-param-reassign */
